import { createRootRoute, Outlet } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'
import { env } from '@/config/env'
import { useSSE } from '@/hooks/useSSE'
import { useNotificationAlerts } from '@/features/notifications/useNotificationAlerts'
import { AutoUpdateOverlay } from '@/components/shared/AutoUpdateOverlay'
import { MiniPlayer } from '@/components/shared/MiniPlayer'
import { MigrationBanner } from '@/components/shared/MigrationBanner'
import { NotFoundPage } from '@/components/shared/NotFoundPage'
import { ServerErrorPage } from '@/components/shared/ServerErrorPage'

export const Route = createRootRoute({
  component: RootLayout,
  // AC-12: 404 + route-loader-error handlers. Render-time exceptions are
  // still caught by the React `<ErrorBoundary>` mounted in main.tsx.
  notFoundComponent: NotFoundPage,
  errorComponent: ServerErrorPage,
})

function RootLayout() {
  // The single global SSE channel lives at the root of the React tree.
  // Per-feature components MUST NOT instantiate their own EventSource —
  // browsers cap at 6 connections per origin, see architecture invariant
  // C-07 / PAIN_POINTS #9. Internally `useSSE` no-ops while the user is
  // unauthenticated or has not yet picked a workspace.
  useSSE()
  // Global: corner toast + chime whenever a new notification arrives (a gate
  // needing confirmation — озвучка / монтаж / платні — or any other).
  useNotificationAlerts()
  return (
    <>
      {/* Dev-server migration notice — see MigrationBanner for the layout
          offsets it forces on Header / Sidebar / Shell / auth pages. */}
      <MigrationBanner />
      <Outlet />
      {/* Story E01.11 AC-06: single global toast host. `richColors` opts in
          to sonner's pre-themed success/error/warning palettes which align
          closely with our design tokens; full visual parity with legacy
          toast styles can be tuned in a later story. */}
      <Toaster position="top-right" richColors closeButton />
      {/* Story E01.12 AC-10: AutoUpdateOverlay sits at the root so any
          authenticated screen gets the 5-second countdown when the
          backend reports a new build. The hook itself is mounted from
          inside the overlay component (it short-circuits when there's
          no pending update). */}
      <AutoUpdateOverlay />
      {/* Story E03.05: floating mini-player. Mounted globally so any
          row in /voices, /voices history, etc. can hand a track off to
          it via `usePlayerStore.play()`. */}
      <MiniPlayer />
      {env.isDev && (
        <>
          <TanStackRouterDevtools position="bottom-right" />
          <ReactQueryDevtools buttonPosition="bottom-left" />
        </>
      )}
    </>
  )
}
